import React, { useState, useEffect, useCallback } from 'react';
import api from '../../config/api';
import { 
  CreditCard, DollarSign, CheckCircle, XCircle, 
  Clock, Search, Download, RefreshCw, Mail,
  TrendingUp, Calendar, Phone, User, Filter,
  UserCheck, Trash2, Package, X, Plus, Link, IndianRupee
} from 'lucide-react';
import './Payments.css';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sendingEmail, setSendingEmail] = useState(null);
  
  // Approve Modal State
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedBundles, setSelectedBundles] = useState([]);
  
  // Database bundles (fetched from admin)
  const [availableBundles, setAvailableBundles] = useState([]);
  
  // Custom bundles state (for adding temporary custom bundles in approve modal)
  const [customBundles, setCustomBundles] = useState([]);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomBundle, setNewCustomBundle] = useState({ name: '', price: '', link: '' });

  // Fetch available bundles from database
  const fetchBundles = async () => {
    try {
      const response = await api.get('/payments/bundles/available');
      if (response.data.success) {
        setAvailableBundles(response.data.bundles);
      }
    } catch (err) {
      console.error('Failed to fetch bundles:', err);
    }
  };

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(search && { search })
      });
      
      const response = await api.get(`/payments?${params}`);
      setPayments(response.data.payments || []);
      setTotalPages(Math.ceil((response.data.total || 0) / 20));
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, search]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/payments/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchStats();
    fetchBundles();
  }, [fetchPayments]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPayments();
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/payments/export/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export payments');
    }
  };

  const handleResendEmail = async (paymentId) => {
    try {
      setSendingEmail(paymentId);
      await api.post(`/payments/${paymentId}/resend-email`);
      alert('Email sent successfully!');
      fetchPayments();
    } catch (err) {
      console.error('Failed to resend email:', err);
      alert('Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const [approvingPayment, setApprovingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);

  // Get all bundles (database bundles + custom temporary bundles)
  const getAllBundles = () => [...availableBundles.map(b => ({ ...b, id: b.id })), ...customBundles];

  // Open approve modal with payment
  const openApproveModal = (payment) => {
    setSelectedPayment(payment);
    setSelectedBundles([]);
    setCustomBundles([]);
    setShowAddCustom(false);
    setNewCustomBundle({ name: '', price: '', link: '' });
    setShowApproveModal(true);
  };

  // Close approve modal
  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedPayment(null);
    setSelectedBundles([]);
    setCustomBundles([]);
    setShowAddCustom(false);
  };

  // Toggle bundle selection
  const toggleBundle = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId) 
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
  };

  // Add custom bundle
  const addCustomBundle = () => {
    if (!newCustomBundle.name || !newCustomBundle.price) {
      alert('Please enter bundle name and price');
      return;
    }
    const customId = `custom-${Date.now()}`;
    setCustomBundles(prev => [...prev, {
      id: customId,
      name: newCustomBundle.name,
      price: parseFloat(newCustomBundle.price) || 0,
      link: newCustomBundle.link || '',
      isCustom: true
    }]);
    setSelectedBundles(prev => [...prev, customId]); // Auto-select new custom bundle
    setNewCustomBundle({ name: '', price: '', link: '' });
    setShowAddCustom(false);
  };

  // Remove custom bundle
  const removeCustomBundle = (bundleId) => {
    setCustomBundles(prev => prev.filter(b => b.id !== bundleId));
    setSelectedBundles(prev => prev.filter(id => id !== bundleId));
  };

  // Calculate total additional amount
  const calculateAdditionalAmount = () => {
    const allBundles = getAllBundles();
    return selectedBundles.reduce((total, bundleId) => {
      const bundle = allBundles.find(b => b.id === bundleId);
      return total + (bundle ? bundle.price : 0);
    }, 0);
  };

  // Get selected bundles data for API
  const getSelectedBundlesData = () => {
    const allBundles = getAllBundles();
    return selectedBundles.map(id => {
      const bundle = allBundles.find(b => b.id === id);
      return bundle ? { id: bundle.id, name: bundle.name, price: bundle.price, link: bundle.link } : null;
    }).filter(Boolean);
  };

  const handleApprovePayment = async () => {
    if (!selectedPayment) return;
    
    const additionalAmount = calculateAdditionalAmount();
    const bundlesData = getSelectedBundlesData();
    const bundleNames = bundlesData.map(b => b.name).join(', ');
    
    const confirmMessage = selectedBundles.length > 0
      ? `Approve payment and create account for ${selectedPayment.name}?\n\nSelected Bundles: ${bundleNames}\nAdditional Amount: ₹${additionalAmount.toLocaleString()}\nTotal: ₹${(selectedPayment.amount + additionalAmount).toLocaleString()}\n\nDefault password: ${selectedPayment.name.split(' ')[0].toLowerCase()}@123`
      : `Approve payment and create account for ${selectedPayment.name}?\n\nThis will:\n- Create a user account\n- Send login credentials via email\n- Password will be: ${selectedPayment.name.split(' ')[0].toLowerCase()}@123`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setApprovingPayment(selectedPayment.id);
      const response = await api.post(`/payments/${selectedPayment.id}/approve`, {
        bundles: selectedBundles,
        bundlesData: bundlesData // Send full bundle data for custom bundles
      });
      
      let successMessage = `✅ Payment approved!\n\nUser account created.\nDefault password: ${response.data.defaultPassword}`;
      if (response.data.bundlesIncluded && response.data.bundlesIncluded.length > 0) {
        successMessage += `\n\nBundles included:\n${response.data.bundlesIncluded.join('\n')}`;
        successMessage += `\n\nTotal Amount: ₹${response.data.totalAmount.toLocaleString()}`;
      }
      successMessage += `\n\nEmail with credentials has been sent.`;
      
      alert(successMessage);
      closeApproveModal();
      fetchPayments();
      fetchStats();
    } catch (err) {
      console.error('Failed to approve payment:', err);
      alert(err.response?.data?.error || 'Failed to approve payment');
    } finally {
      setApprovingPayment(null);
    }
  };

  const handleApprovePaymentDirect = async (paymentId, paymentName) => {
    if (!window.confirm(`Approve payment and create account for ${paymentName}?\n\nThis will:\n- Create a user account\n- Send login credentials via email\n- Password will be: ${paymentName.split(' ')[0].toLowerCase()}@123`)) {
      return;
    }
    
    try {
      setApprovingPayment(paymentId);
      const response = await api.post(`/payments/${paymentId}/approve`, { bundles: [] });
      alert(`✅ Payment approved!\n\nUser account created.\nDefault password: ${response.data.defaultPassword}\n\nEmail with credentials has been sent.`);
      fetchPayments();
      fetchStats();
    } catch (err) {
      console.error('Failed to approve payment:', err);
      alert(err.response?.data?.error || 'Failed to approve payment');
    } finally {
      setApprovingPayment(null);
    }
  };

  const handleDeletePayment = async (paymentId, paymentName) => {
    if (!window.confirm(`Are you sure you want to delete payment for ${paymentName}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      setDeletingPayment(paymentId);
      await api.delete(`/payments/${paymentId}`);
      alert('Payment deleted successfully');
      fetchPayments();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete payment:', err);
      alert(err.response?.data?.error || 'Failed to delete payment');
    } finally {
      setDeletingPayment(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(dateStr));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { class: 'status-success', icon: <CheckCircle size={14} />, label: 'Success' },
      approved: { class: 'status-approved', icon: <UserCheck size={14} />, label: 'Approved' },
      failed: { class: 'status-failed', icon: <XCircle size={14} />, label: 'Failed' },
      pending: { class: 'status-pending', icon: <Clock size={14} />, label: 'Pending' },
      initiated: { class: 'status-initiated', icon: <Clock size={14} />, label: 'Initiated' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge ${config.class}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  if (loading && payments.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="page-header">
        <div className="header-content">
          <h1><CreditCard size={28} /> Payment Records</h1>
          <p>Track all payment transactions and their status</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => { fetchPayments(); fetchStats(); }}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={handleExport}>
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <h3>{formatCurrency(stats?.total_revenue)}</h3>
            <p>Total Revenue</p>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.success_count || 0}</h3>
            <p>Successful Payments</p>
          </div>
        </div>

        <div className="stat-card approved">
          <div className="stat-icon">
            <UserCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.approved_count || 0}</h3>
            <p>Approved (Account Created)</p>
          </div>
        </div>

        <div className="stat-card failed">
          <div className="stat-icon">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.failed_count || 0}</h3>
            <p>Failed Payments</p>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.pending_count || 0}</h3>
            <p>Pending Payments</p>
          </div>
        </div>

        <div className="stat-card initiated">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.initiated_count || 0}</h3>
            <p>Initiated (Form Filled)</p>
          </div>
        </div>

        <div className="stat-card today">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>{formatCurrency(stats?.today_revenue)}</h3>
            <p>Today's Revenue ({stats?.today_count || 0} payments)</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name, email, phone, or payment ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <div className="filter-group">
          <Filter size={18} />
          <select 
            value={statusFilter} 
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="approved">Approved</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="initiated">Initiated</option>
          </select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Customer</th>
              <th>Contact</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Login Password</th>
              <th>Payment ID</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  <CreditCard size={48} />
                  <p>No payment records found</p>
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id} className={`payment-row ${payment.status}`}>
                  <td className="payment-id">#{payment.id}</td>
                  <td className="customer-info">
                    <div className="customer-name">
                      <User size={14} />
                      {payment.name}
                    </div>
                  </td>
                  <td className="contact-info">
                    <div className="contact-email">{payment.email}</div>
                    <div className="contact-phone">
                      <Phone size={12} />
                      {payment.phone}
                    </div>
                  </td>
                  <td className="product-name">{payment.product_name || 'N/A'}</td>
                  <td className="amount">{formatCurrency(payment.amount)}</td>
                  <td>{getStatusBadge(payment.status)}</td>
                  <td className="user-password">
                    {payment.user_password ? (
                      <code className="password-code">{payment.user_password}</code>
                    ) : (
                      <span className="no-password">-</span>
                    )}
                  </td>
                  <td className="razorpay-id">
                    {payment.razorpay_payment_id ? (
                      <code>{payment.razorpay_payment_id}</code>
                    ) : (
                      <span className="no-id">-</span>
                    )}
                  </td>
                  <td className="date">
                    <Calendar size={12} />
                    {formatDate(payment.created_at)}
                  </td>
                  <td className="actions">
                    {/* Approve Button - show only for pending/initiated/success status */}
                    {(payment.status === 'pending' || payment.status === 'initiated' || payment.status === 'success') && (
                      <>
                        <button
                          className="btn-action btn-approve"
                          onClick={() => openApproveModal(payment)}
                          disabled={approvingPayment === payment.id}
                          title="Approve with Bundles"
                        >
                          {approvingPayment === payment.id ? (
                            <RefreshCw size={14} className="spinning" />
                          ) : (
                            <Package size={14} />
                          )}
                          <span>Approve+</span>
                        </button>
                        <button
                          className="btn-action btn-approve-quick"
                          onClick={() => handleApprovePaymentDirect(payment.id, payment.name)}
                          disabled={approvingPayment === payment.id}
                          title="Quick Approve (No Bundles)"
                        >
                          {approvingPayment === payment.id ? (
                            <RefreshCw size={14} className="spinning" />
                          ) : (
                            <UserCheck size={14} />
                          )}
                          <span>Quick</span>
                        </button>
                      </>
                    )}
                    
                    {/* Already Approved Badge */}
                    {payment.status === 'approved' && (
                      <span className="approved-badge">
                        <CheckCircle size={14} />
                        Approved
                      </span>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDeletePayment(payment.id, payment.name)}
                      disabled={deletingPayment === payment.id}
                      title="Delete Payment"
                    >
                      {deletingPayment === payment.id ? (
                        <RefreshCw size={14} className="spinning" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                      <span>Delete</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Approve Modal with Bundle Selection */}
      {showApproveModal && selectedPayment && (
        <div className="modal-overlay">
          <div className="approve-modal">
            <div className="modal-header">
              <h2>
                <UserCheck size={24} />
                Approve Payment
              </h2>
              <button className="close-btn" onClick={closeApproveModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Payment Info */}
              <div className="payment-info-card">
                <h3>Payment Details</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Customer</span>
                    <span className="value">{selectedPayment.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Email</span>
                    <span className="value">{selectedPayment.email}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Product</span>
                    <span className="value">{selectedPayment.product_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Amount Paid</span>
                    <span className="value amount">{formatCurrency(selectedPayment.amount)}</span>
                  </div>
                </div>
              </div>
              
              {/* Bundle Selection */}
              <div className="bundle-selection">
                <div className="bundle-header">
                  <h3>
                    <Package size={18} />
                    Add Extra Bundles (Optional)
                  </h3>
                  <button 
                    className="btn-add-custom"
                    onClick={() => setShowAddCustom(!showAddCustom)}
                  >
                    <Plus size={16} />
                    Add Custom
                  </button>
                </div>
                <p className="bundle-info">Select additional courses/kits to include with this approval. Links will be sent via email.</p>
                
                {/* Add Custom Bundle Form */}
                {showAddCustom && (
                  <div className="custom-bundle-form">
                    <h4>Add Custom Bundle</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Bundle Name *</label>
                        <input
                          type="text"
                          placeholder="e.g., Advanced Marketing Kit"
                          value={newCustomBundle.name}
                          onChange={(e) => setNewCustomBundle(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Price (₹) *</label>
                        <input
                          type="number"
                          placeholder="e.g., 2500"
                          value={newCustomBundle.price}
                          onChange={(e) => setNewCustomBundle(prev => ({ ...prev, price: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="form-group full-width">
                      <label>Access Link (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., https://example.com/course"
                        value={newCustomBundle.link}
                        onChange={(e) => setNewCustomBundle(prev => ({ ...prev, link: e.target.value }))}
                      />
                    </div>
                    <div className="form-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowAddCustom(false)}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={addCustomBundle}>
                        <Plus size={14} />
                        Add Bundle
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="bundle-list">
                  {/* Available Bundles from Database */}
                  {availableBundles.length > 0 ? (
                    <>
                      <div className="bundle-section-title">Available Courses</div>
                      {availableBundles.map(bundle => (
                        <label 
                          key={bundle.id} 
                          className={`bundle-item ${selectedBundles.includes(bundle.id) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBundles.includes(bundle.id)}
                            onChange={() => toggleBundle(bundle.id)}
                          />
                          <div className="bundle-details">
                            <div className="bundle-name-row">
                              <span className="bundle-name">{bundle.name}</span>
                              <span className="bundle-price">₹{parseFloat(bundle.price).toLocaleString('en-IN')}</span>
                            </div>
                            {bundle.link && (
                              <div className="bundle-link-row">
                                <Link size={12} />
                                <span className="bundle-link">{bundle.link}</span>
                              </div>
                            )}
                          </div>
                          <CheckCircle size={18} className="check-icon" />
                        </label>
                      ))}
                    </>
                  ) : (
                    <div className="no-bundles-message">
                      No bundles available. Add bundles from the <a href="/admin/bundles">Bundles</a> page.
                    </div>
                  )}
                  
                  {/* Custom Bundles */}
                  {customBundles.length > 0 && (
                    <>
                      <div className="bundle-section-title custom">Custom Bundles</div>
                      {customBundles.map(bundle => (
                        <label 
                          key={bundle.id} 
                          className={`bundle-item custom ${selectedBundles.includes(bundle.id) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBundles.includes(bundle.id)}
                            onChange={() => toggleBundle(bundle.id)}
                          />
                          <div className="bundle-details">
                            <div className="bundle-name-row">
                              <span className="bundle-name">{bundle.name}</span>
                              <span className="bundle-price">₹{bundle.price.toLocaleString('en-IN')}</span>
                            </div>
                            {bundle.link && (
                              <div className="bundle-link-row">
                                <Link size={12} />
                                <span className="bundle-link">{bundle.link}</span>
                              </div>
                            )}
                          </div>
                          <button 
                            className="btn-remove-bundle"
                            onClick={(e) => { e.preventDefault(); removeCustomBundle(bundle.id); }}
                            title="Remove custom bundle"
                          >
                            <Trash2 size={14} />
                          </button>
                          <CheckCircle size={18} className="check-icon" />
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
              
              {/* Total Summary */}
              <div className="total-summary">
                <div className="summary-row">
                  <span>Original Amount</span>
                  <span>{formatCurrency(selectedPayment.amount)}</span>
                </div>
                {selectedBundles.length > 0 && (
                  <div className="summary-row additional">
                    <span>Additional Bundles ({selectedBundles.length})</span>
                    <span>+{formatCurrency(calculateAdditionalAmount())}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total Value</span>
                  <span>{formatCurrency(selectedPayment.amount + calculateAdditionalAmount())}</span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeApproveModal}>
                Cancel
              </button>
              <button 
                className="btn btn-primary btn-approve-confirm"
                onClick={handleApprovePayment}
                disabled={approvingPayment}
              >
                {approvingPayment ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Approving...
                  </>
                ) : (
                  <>
                    <UserCheck size={16} />
                    Approve & Send Credentials
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
